import { useState } from 'react';
import Select from 'react-select';
import { STAGE_OPTIONS, STAGE_PILL_GROUP_LABELS } from '../options';
import { reilmAPI } from '../../../services/apiClient';
import { getStatusLabel, resolveSelectOption } from '../utils';
import { STAGE_PILL, pillIndicator, pillStyles } from './pillSelect';

const DropdownIndicator = pillIndicator(STAGE_PILL);
const styles = pillStyles(STAGE_PILL);

// Pill value: group 13/600 over stage 12/400, both white (spec.md b). A fallback option (legacy
// stored value, no stageLabel) and menu rows render one line.
const formatOptionLabel = ({ label, stageLabel }, { context }) => {
  if (context === 'value' && stageLabel) {
    return (
      <div className="flex flex-col justify-center min-w-0 text-white">
        <span className="text-[13px] font-semibold leading-[16px] truncate">{stageLabel}</span>
        <span className="text-[12px] font-normal leading-[16px] truncate">{label}</span>
      </div>
    );
  }
  if (context === 'value') {
    return <span className="block text-[12px] font-normal text-white leading-none truncate">{label}</span>;
  }
  return <span className="text-[13px] text-brand-dark leading-none">{label}</span>;
};

// Kept as named exports: StageCellEditor.test.jsx pins the strut fix on them (README.md).
export const singleValueStyle = styles.singleValue;
export const valueContainerStyle = styles.valueContainer;

const StageCellEditor = ({ params, activeStage, onStageUpdate, onSaveOk, onSaveFailed }) => {
  const [saving, setSaving] = useState(false);

  const groupedOptions = (activeStage === 'all' ? STAGE_OPTIONS : STAGE_OPTIONS.filter(g => g.key === activeStage))
    .map(g => ({ ...g, options: g.options.map(o => ({ ...o, stageLabel: STAGE_PILL_GROUP_LABELS[g.key] })) }))
  // row.stage is the backend field name (property.stage). A value with no canonical match still
  // renders via resolveSelectOption's passthrough, not vanish into the placeholder.
  const currentOption = resolveSelectOption(groupedOptions, params.row.stage, getStatusLabel);

  const handleChange = async (selected) => {
    if (!selected || selected.value === params.row.stage) return;
    setSaving(true);
    try {
      await reilmAPI.updatedPropertiesDetails(params.row.id, { stage: selected.value });
      onStageUpdate(params.row.id, selected.value);
      onSaveOk?.(`stage:${params.row.id}`);
    } catch (err) {
      // Row stays unchanged (onStageUpdate above never ran) -- isDisabled={saving} below already
      // rules out a race on this control, so there is nothing to roll back, only the shared banner
      // (src/pages/README.md) to raise.
      onSaveFailed?.(`stage:${params.row.id}`, err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Select
      value={currentOption}
      options={groupedOptions}
      onChange={handleChange}
      isDisabled={saving}
      formatOptionLabel={formatOptionLabel}
      components={{ DropdownIndicator, IndicatorSeparator: null }}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      placeholder="Select"
      styles={styles}
    />
  );
};

export default StageCellEditor;
